import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { ksefService } from '../../services/ksefService';
import { KSeFConfigModal } from './KSeFConfigModal';
import type { BusinessProfileDetail, KSeFAuthMode } from '../../types/businessProfile';

interface KSeFSectionProps {
  businessProfile: BusinessProfileDetail | null;
  isCollapsed?: boolean;
  onToggle?: () => void;
  onProfileUpdate: () => void;
  /** Related one-off invoicing settings rendered below the KSeF card. */
  children?: ReactNode;
}

export function KSeFSection({
  businessProfile,
  isCollapsed = false,
  onToggle,
  onProfileUpdate,
  children,
}: KSeFSectionProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { showToast } = useToast();

  const [showConfigModal, setShowConfigModal] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  if (!businessProfile) {
    return null;
  }

  const isConfigured = businessProfile.kSeFTokenConfigured || businessProfile.kSeFCertificateConfigured || false;
  const isEnabled = businessProfile.kSeFEnabled || false;
  const environment = businessProfile.kSeFEnvironment;
  const authMode: KSeFAuthMode = businessProfile.kSeFAuthMode || 'Token';
  const hasCertificate = businessProfile.kSeFCertificateConfigured || false;
  const certificateSubject = businessProfile.kSeFCertificateSubject;
  const certificateExpiresAt = businessProfile.kSeFCertificateExpiresAt;
  const certificateDaysRemaining = businessProfile.kSeFCertificateDaysRemaining;
  const lastSync = businessProfile.kSeFLastSyncAt;

  // Expiry warnings
  const certificateExpiryWarning = certificateDaysRemaining !== null && certificateDaysRemaining !== undefined && certificateDaysRemaining <= 30 && certificateDaysRemaining > 7;
  const certificateExpiryCritical = certificateDaysRemaining !== null && certificateDaysRemaining !== undefined && certificateDaysRemaining <= 7 && certificateDaysRemaining > 0;
  const certificateExpired = certificateDaysRemaining !== null && certificateDaysRemaining !== undefined && certificateDaysRemaining <= 0;

  const formatLastSync = (dateString?: string) => {
    if (!dateString) return t('ksef.neverSynced', 'Nigdy');
    try {
      const date = new Date(dateString);
      return date.toLocaleString('pl-PL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return t('ksef.neverSynced', 'Nigdy');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pl-PL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch {
      return '-';
    }
  };

  const handleToggleEnabled = async () => {
    if (!token || !businessProfile.id) return;

    setIsToggling(true);
    try {
      await ksefService.enableKSeF(businessProfile.id, !isEnabled, token);
      showToast(
        'success',
        isEnabled
          ? t('ksef.disabled', 'KSeF wyłączony')
          : t('ksef.enabled', 'KSeF włączony')
      );
      onProfileUpdate();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to toggle KSeF';
      showToast('error', t('common.error'), message);
    } finally {
      setIsToggling(false);
    }
  };

  const handleConfigured = () => {
    setShowConfigModal(false);
    onProfileUpdate();
    showToast('success', t('ksef.configuredSuccess', 'KSeF skonfigurowany pomyślnie'));
  };

  return (
    <div className="bg-brand-primary-deep rounded-lg overflow-hidden">
      {/* Section Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/10 transition-colors"
      >
        <div className="flex items-center gap-3">
          <svg className="w-5 h-5 text-brand-primary-light" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="text-lg font-medium text-white">
            {t('ksef.title', 'Integracja KSeF')}
          </span>
          {isConfigured && (
            <span className={`px-2 py-0.5 rounded-full text-xs ${isEnabled ? 'bg-brand-confirm text-white' : 'bg-white/15 text-white/80'}`}>
              {isEnabled ? t('ksef.active', 'Aktywny') : t('ksef.inactive', 'Nieaktywny')}
            </span>
          )}
        </div>
        <svg
          className={`w-5 h-5 text-white/70 transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Section Content */}
      {!isCollapsed && (
        <div className="px-4 pb-4 space-y-4">
          <div className="bg-surface-card rounded-lg p-4 space-y-4">
            {/* Configuration Status */}
            <div className="flex items-center gap-2">
              {isConfigured ? (
                <svg className="w-5 h-5 text-brand-confirm-deep" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-ink-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              <span className="text-sm text-ink-primary">
                {isConfigured
                  ? t('ksef.configured', 'Skonfigurowano')
                  : t('ksef.notConfigured', 'Nie skonfigurowano')}
              </span>
            </div>

            {/* Auth Mode Badge */}
            {isConfigured && (
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${authMode === 'Certificate' ? 'bg-brand-primary/15 text-brand-primary' : 'bg-surface-app-elevated text-ink-primary'}`}>
                  {authMode === 'Certificate' ? (
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  ) : (
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                  )}
                  {authMode === 'Certificate'
                    ? t('ksef.authModeCertificate', 'Certyfikat')
                    : t('ksef.authModeToken', 'Token API')}
                </span>
              </div>
            )}

            {/* Environment Badge */}
            {isConfigured && environment && (
              <div className="flex items-center gap-2">
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${environment === 'Production' ? 'bg-brand-warm/30 text-brand-warm-deep' : 'bg-brand-accent/20 text-brand-accent'}`}>
                  {environment === 'Test'
                    ? t('ksef.environmentTest', 'Środowisko testowe')
                    : t('ksef.environmentProduction', 'Środowisko produkcyjne')}
                </span>
              </div>
            )}

            {/* Certificate Info */}
            {isConfigured && authMode === 'Certificate' && hasCertificate && (
              <div className="bg-surface-app-elevated rounded-lg p-3 space-y-2">
                <div>
                  <span className="text-xs text-ink-secondary">{t('ksef.certificateSubject', 'Podmiot certyfikatu')}:</span>
                  <p className="text-sm text-ink-primary truncate">{certificateSubject || '-'}</p>
                </div>
                <div>
                  <span className="text-xs text-ink-secondary">{t('ksef.certificateExpires', 'Wygasa')}:</span>
                  <p className={`text-sm font-medium ${certificateExpired ? 'text-brand-warm-deep' : certificateExpiryCritical ? 'text-brand-warm-deep' : certificateExpiryWarning ? 'text-brand-accent' : 'text-brand-confirm-deep'}`}>
                    {formatDate(certificateExpiresAt)}
                    {certificateDaysRemaining !== null && certificateDaysRemaining !== undefined && (
                      <span className="ml-2">
                        {certificateExpired
                          ? `(${t('ksef.expired', 'Wygasł')})`
                          : `(${certificateDaysRemaining} ${t('ksef.daysRemaining', 'dni')})`}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            )}

            {/* Certificate Expiry Warning */}
            {isConfigured && authMode === 'Certificate' && (certificateExpiryWarning || certificateExpiryCritical || certificateExpired) && (
              <div className={`flex items-start gap-2 p-3 rounded-lg ${certificateExpired || certificateExpiryCritical ? 'bg-brand-warm/10 border border-brand-warm/30' : 'bg-brand-accent/10 border border-brand-accent/30'}`}>
                <svg className={`w-5 h-5 flex-shrink-0 ${certificateExpired || certificateExpiryCritical ? 'text-brand-warm-deep' : 'text-brand-accent'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <p className={`text-sm font-medium ${certificateExpired || certificateExpiryCritical ? 'text-brand-warm-deep' : 'text-brand-accent'}`}>
                    {certificateExpired
                      ? t('ksef.certificateExpiredTitle', 'Certyfikat wygasł')
                      : certificateExpiryCritical
                        ? t('ksef.certificateExpiryCriticalTitle', 'Certyfikat wygasa wkrótce!')
                        : t('ksef.certificateExpiryWarningTitle', 'Certyfikat wkrótce wygaśnie')}
                  </p>
                  <p className={`text-xs mt-1 ${certificateExpired || certificateExpiryCritical ? 'text-brand-warm-deep' : 'text-brand-accent'}`}>
                    {certificateExpired
                      ? t('ksef.certificateExpiredMessage', 'Załaduj nowy certyfikat, aby kontynuować korzystanie z KSeF.')
                      : t('ksef.certificateExpiryMessage', 'Zalecamy zaktualizowanie certyfikatu przed wygaśnięciem.')}
                  </p>
                </div>
              </div>
            )}

            {/* Last Sync */}
            {isConfigured && (
              <div className="text-sm text-ink-secondary">
                {t('ksef.lastSync', 'Ostatnia synchronizacja')}: {formatLastSync(lastSync)}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-3 pt-2">
              {isConfigured && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-ink-primary">
                    {isEnabled ? t('ksef.enabledLabel', 'Włączony') : t('ksef.disabledLabel', 'Wyłączony')}
                  </span>
                  <button
                    onClick={handleToggleEnabled}
                    disabled={isToggling}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 focus:ring-offset-surface-card ${isEnabled ? 'bg-brand-primary' : 'bg-surface-app-elevated'} ${isToggling ? 'opacity-50 cursor-not-allowed' : ''}`}
                    role="switch"
                    aria-checked={isEnabled}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isEnabled ? 'translate-x-5' : 'translate-x-0'}`}
                    />
                  </button>
                </div>
              )}
              <button
                onClick={() => setShowConfigModal(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-brand-primary text-white hover:bg-brand-primary-hover transition-colors text-sm"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {isConfigured
                  ? t('ksef.reconfigure', 'Rekonfiguruj')
                  : t('ksef.configure', 'Konfiguruj')}
              </button>
            </div>
          </div>

          {children}
        </div>
      )}

      {/* Config Modal */}
      {showConfigModal && businessProfile.id && (
        <KSeFConfigModal
          businessProfileId={businessProfile.id}
          currentEnvironment={environment}
          currentAuthMode={authMode}
          onClose={() => setShowConfigModal(false)}
          onConfigured={handleConfigured}
        />
      )}
    </div>
  );
}
